import { Footprints, Route as RouteIcon, Wrench, PartyPopper, Laptop, GraduationCap, Heart, Users, FileCheck } from "lucide-react";
import {
  PageHero,
  Prose,
  SectionHeading,
  IconCardGrid,
  CtaBand,
  RelatedLinks,
  mailto,
} from "@/components/site/PageParts";
import { Reveal } from "@/components/site/Reveal";
import { Button } from "@/components/ui/button";
import { ASSETS, TESTIMONIALS } from "@/data/site";
import { Seo } from "@/components/site/Seo";

const ROLES = [
  { Icon: Footprints, title: "Side-walkers", body: "Walk alongside riders during sessions, offering reassurance and support. No horse experience needed." },
  { Icon: RouteIcon, title: "Horse leaders", body: "Lead our gentle horses and ponies during sessions. Some horse handling experience is helpful." },
  { Icon: Wrench, title: "Yard helpers", body: "Help with mucking out, grooming, tack and keeping the yard safe and tidy." },
  { Icon: PartyPopper, title: "Fundraising & events", body: "Help plan and run fundraising events that keep our charity going." },
  { Icon: Laptop, title: "Admin & digital", body: "Support us behind the scenes with admin, social media and communications." },
];

const BENEFITS = [
  { Icon: GraduationCap, title: "Training", body: "Full training and induction — we'll teach you everything you need." },
  { Icon: Heart, title: "Purpose", body: "See the direct, joyful impact of your time on riders' lives." },
  { Icon: Users, title: "Community", body: "Join a warm, welcoming team of like-minded people." },
  { Icon: FileCheck, title: "References", body: "Gain valuable experience and references for work or study." },
];

export default function Volunteering() {
  const jill = TESTIMONIALS.find((t) => t.name === "Jill");
  return (
    <div data-testid="volunteering-page">
      <Seo title="Volunteering — RDA Glasgow" description="Give a little time and change a life. Explore volunteer roles at RDA Glasgow — side-walkers, horse leaders, yard helpers, fundraising and more. Training provided." image={ASSETS.volunteering} />
      <PageHero
        title="Volunteering"
        subtitle="Give a little time, change a life. Our volunteers make every session possible."
        image={ASSETS.volunteering}
        imageAlt="Volunteers supporting a rider in the indoor school at RDA Glasgow"
      />

      <Prose>
        <SectionHeading eyebrow="Why volunteer" title="Be the reason someone rides" />
        <p>
          Our volunteers are the heart of RDA Glasgow. Whether you're leading a horse,
          walking beside a rider or helping behind the scenes, you'll play a direct part
          in helping disabled people grow in confidence, strength and joy — and you'll
          have a wonderful time doing it.
        </p>
      </Prose>

      {/* Roles */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <SectionHeading eyebrow="Roles we need" title="There's a role for everyone" />
          <div className="mt-10">
            <IconCardGrid cards={ROLES} cols={3} />
          </div>
        </div>
      </section>

      {/* What we ask */}
      <Prose>
        <SectionHeading eyebrow="What we ask" title="A little commitment goes a long way" />
        {/* TODO: verify with RDA — typical commitment of one session per week */}
        <p>
          Most volunteers give around one session per week, though we're always happy to
          talk about what works for you. Full training is provided, and for many roles no
          previous horse experience is needed at all — just warmth, reliability and a
          willingness to learn.
        </p>
      </Prose>

      {/* What you'll get */}
      <section className="bg-rda-offwhite">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <SectionHeading eyebrow="What you'll get" title="Volunteering gives back" />
          <div className="mt-10">
            <IconCardGrid cards={BENEFITS} cols={4} />
          </div>
        </div>
      </section>

      {/* Testimonial callout */}
      {jill && (
        <section className="bg-white">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-14">
            <Reveal>
              {/* Seeded from rdaglasgow.org testimonials — verify wording with RDA before launch */}
              <figure className="rounded-3xl border border-border bg-rda-offwhite p-8 md:p-12 text-center">
                <blockquote className="font-heading text-2xl md:text-3xl leading-snug text-rda-ink">
                  “{jill.quote}”
                </blockquote>
                <figcaption className="mt-5 text-sm font-semibold text-rda-teal">
                  {jill.name}
                  <span className="font-normal text-rda-muted"> · {jill.role}</span>
                </figcaption>
              </figure>
            </Reveal>
          </div>
        </section>
      )}

      <CtaBand
        title="Ready to join the team?"
        subtitle="Email us and we'll tell you everything you need to know about getting started."
        action={
          <Button
            asChild
            size="lg"
            className="rounded-full bg-white text-rda-tealDark hover:bg-white/90 font-semibold px-7 h-12"
            data-testid="volunteer-apply-cta"
          >
            <a href={mailto("Volunteering enquiry")}>Apply to volunteer</a>
          </Button>
        }
        testId="volunteering-cta-band"
      />

      <RelatedLinks
        links={[
          { title: "Riding & Therapy", description: "See the sessions your time helps make possible.", to: "/riding-therapy" },
          { title: "Vacancies", description: "We're also recruiting charity trustees.", to: "/vacancies" },
          { title: "Support Us", description: "Other ways to help beyond volunteering.", to: "/support-us" },
        ]}
      />
    </div>
  );
}
